/**
 * The client's copy of the conversation zip's name and its one refusal
 * (#1069 Story 7.5, D5, D7).
 *
 * ⚠️ A MIRROR OF `server/services/correspondenceZipName.ts`, AND THE PARITY
 * TEST IS THE POINT. The client computes the zip's local file name itself from
 * `requestId` and the open thread's title, because `Content-Disposition` is
 * not a CORS-safelisted response header (nothing exposes it to an XHR) and
 * native blob-util reads headers only after the body has landed. A pure
 * function on fixed inputs is a contract a test can hold; a header the client
 * cannot read is not. `zipFileName.test.ts` imports the server module and
 * pins both functions byte-identical, the `messageFileBytes.test.ts` way:
 * `src/` cannot import `server/` at runtime and `server/` must never import
 * `src/`.
 *
 * `zipTooLargeReason` is the sentence the panel shows when it refuses a "Save
 * all" locally (AC 15), and the same sentence the server's `413` carries, so a
 * refusal reads the same whichever side made it.
 */

import { MAX_ZIP_BYTES } from './messageFileLimits';
import { formatFileSize } from '../../utils/attachmentFormatters';

/** The slug's ceiling. Mirrors the server's `SLUG_MAX_LENGTH`. */
const SLUG_MAX_LENGTH = 40;

/**
 * D3's slug: lower-case, runs of anything outside `[a-z0-9]` become one `-`,
 * trimmed of `-` at both ends, capped at forty characters with no trailing
 * `-` left by the cut, and `''` when nothing survives (Story 7.7, D5).
 */
const slugTitle = (title: string): string =>
  title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, SLUG_MAX_LENGTH)
    .replace(/-+$/, '');

/**
 * When the slug cannot stand for the title (Story 7.7 review, R3), mirrored
 * from the server: nothing survived; no ASCII letter survived ("Охлаждение 2"
 * slugs to `2`); or the slug spells the fallback itself ("thread 57" on
 * thread 12). The parity suite pins all three against literals on both tiers.
 */
const slugNeedsFallback = (slug: string): boolean =>
  slug === '' || !/[a-z]/.test(slug) || /^thread-\d+$/.test(slug);

/**
 * `${requestId}-${slug(title)}-files.zip` (D3), or
 * `${requestId}-thread-${threadId}-files.zip` when the slug cannot stand for
 * the title (Story 7.7, D5, and the review's R3): a Cyrillic or CJK title no
 * longer collapses two conversations onto one name on the reader's disk.
 * Mirrors the server's signature; the third parameter is REQUIRED on both
 * tiers.
 */
export const zipFileName = (
  requestId: number | string,
  title: string,
  threadId: number | string,
): string => {
  const slug = slugTitle(title);
  return `${requestId}-${
    slugNeedsFallback(slug) ? `thread-${threadId}` : slug
  }-files.zip`;
};

/**
 * The one sentence both sides show for a set over `MAX_ZIP_BYTES` (D5), with
 * both sizes through `formatFileSize`, the formatter the rest of the UI uses.
 */
export const zipTooLargeReason = (totalBytes: number): string =>
  `A zip of these files would be ${formatFileSize(
    totalBytes,
  )}, more than the ${formatFileSize(
    MAX_ZIP_BYTES,
  )} a zip may hold. Save them one at a time.`;
